/**
 * DocumentVersionsPage - Page for viewing document version history
 *
 * Accessible via /admin/records/:id/versions route.
 * Shows a sidebar with version list and content area for selected version.
 */
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileText, Loader2 } from "lucide-react";
import { VersionHistory } from "../../components/admin/versions/VersionHistory";
import { useRecordsApi } from "../../lib/api/records";

export function DocumentVersionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getRecord } = useRecordsApi();

  const documentId = id ? parseInt(id, 10) : null;

  // Fetch document info for header
  const {
    data: document,
    isLoading: isLoadingDocument,
    error: documentError,
  } = useQuery({
    queryKey: ["document", documentId],
    queryFn: () => getRecord(id!),
    enabled: !!id,
  });

  if (!documentId || isNaN(documentId)) {
    return (
      <div className="flex-1 flex items-center justify-center bg-brand-surface">
        <div className="text-center p-6">
          <FileText className="h-12 w-12 mx-auto mb-4 text-brand-border" />
          <p className="text-brand-muted font-medium">Invalid document ID</p>
          <Link
            to="/admin/records"
            className="mt-4 inline-flex items-center text-brand-primary hover:opacity-80"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Records
          </Link>
        </div>
      </div>
    );
  }

  if (documentError) {
    return (
      <div className="flex-1 flex items-center justify-center bg-brand-surface">
        <div className="text-center p-6">
          <p className="text-brand-error font-medium">Failed to load document</p>
          <p className="text-brand-muted text-sm mt-1">
            {documentError instanceof Error ? documentError.message : "Unknown error"}
          </p>
          <Link
            to="/admin/records"
            className="mt-4 inline-flex items-center text-brand-primary hover:opacity-80"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Records
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-brand-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/records")}
              className="p-2 text-brand-muted hover:text-brand-muted hover:bg-brand-surface rounded-lg transition-colors"
              title="Back to Records"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              {isLoadingDocument ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-brand-muted" />
                  <span className="text-brand-muted">Loading...</span>
                </div>
              ) : (
                <>
                  <h1 className="text-xl font-semibold text-brand-text">
                    Version History
                  </h1>
                  <p className="text-sm text-brand-muted mt-0.5 truncate max-w-xl">
                    {document?.title || `Document #${documentId}`}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={`/admin/records/${id}/edit`}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-primary bg-brand-primary/5 rounded-lg hover:bg-brand-primary/10 transition-colors"
            >
              Edit Current Version
            </Link>
          </div>
        </div>
      </div>

      {/* Version History Container */}
      <div className="flex-1 overflow-hidden">
        <VersionHistory
          documentId={documentId}
          documentTitle={document?.title}
        />
      </div>
    </div>
  );
}
